import {
  AccountType,
  Breakpoints,
  Button,
  ButtonSize,
  ButtonStyles,
  ButtonWidth,
  Checkbox,
  DynamicObjectInterface,
  LoaderType,
  NextgenImage,
  useBreakpoint,
  useTranslation,
} from '@nextgen-web-framework/all';
import { useSelector } from 'react-redux';
import { AppState, Logger } from '@nextgen-web-framework/core';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Field, FieldProps, Form, FormikProvider, useFormik } from 'formik';
import {
  ImageReturnType,
  RegistrationPatchRequest,
  RegistrationPatchRequestConsentAcceptedEnum,
  RegistrationValidations,
} from '@commerce-profile-webcomponents/sdk';
import { APICallType } from '../Registration/types/propTypes';
import {
  getRegistrationValidationData,
  registrationValidationSchemafn,
  replaceCoAppFieldValidations,
} from '../../utils/registrationUtils';
import { COAPP_FIELDS_LOGGEDIN, ERROR_GENERIC_MESSAGE } from '../../utils/Constants';
import PersonalIdAndTypeInput from '../PersonalIdAndTypeInput/PersonalIdAndTypeInput';
import SkeletonLoader from '../Common/skeleton-loader/SkeletonLoader';
import { useRegistrationFieldsMasterData } from '../../utils/masterdata';
import RegistrationUsername from '../RegistrationUsername/RegistrationUsername';
import DesktopLayout from '../Common/DesktopLayout/DesktopLayout';
import { ApiErrorModalProps } from '../ApiErrorModal/types/propTypes';
import ApiErrorModal from '../ApiErrorModal/ApiErrorModal';
import { CoApplicantIdStepLatamPropType } from './types/propTypes';
import styles from './CoApplicantIdStepLatam.module.scss';

const CoApplicantIdStepLatam = ({ nextHandler }: CoApplicantIdStepLatamPropType) => {
  const t = useTranslation();
  const viewport = useBreakpoint();
  const logger = Logger.getInstance();
  const registrationFlowData = useSelector((state: AppState) => state.commerceProfile.registrationFlowData);
  const registrationCmsData = useSelector((state: AppState) => state.commerceProfile.registrationCmsData);
  const { coApp } = registrationCmsData;
  const registrationFieldsMasterData = useRegistrationFieldsMasterData();
  const [isPhonePrimaryContact, setIsPhonePrimaryContact] = useState<boolean>();
  const [modalErrorData, setModalErrorData] = useState<ApiErrorModalProps>();
  const { perosonalDetails, isRefreshRestoredData, userData } = registrationFlowData;
  const [image, setImage] = useState<ImageReturnType>();

  useEffect(() => {
    const imageInfo = coApp?.coappRegistrationImage?.find((item: ImageReturnType) => item?.viewport.includes(viewport));
    setImage(imageInfo?.image);
  }, [viewport]);

  const fields = useMemo(
    () =>
      getRegistrationValidationData({
        config: registrationFieldsMasterData as RegistrationValidations[],
        fieldNames: COAPP_FIELDS_LOGGEDIN,
      }),
    [],
  );

  const validationfields = useMemo(() => replaceCoAppFieldValidations(fields), [fields]);

  const validationSchema = () => registrationValidationSchemafn(validationfields, AccountType.BusinessOwner);

  const formik = useFormik({
    initialValues: perosonalDetails,
    onSubmit: () => {
      logger.log('submit');
    },
    validationSchema: validationSchema,
    validateOnBlur: true,
  });

  const { values, resetForm } = formik;

  const nextHandlerCallback = useCallback(
    async (payload: DynamicObjectInterface, stayOnSameStep?: boolean) => {
      const response = await nextHandler?.(payload, undefined, stayOnSameStep ?? true, false, APICallType.PATCH);
      if (response?.responseBody?.code) {
        setModalErrorData({
          headline: ERROR_GENERIC_MESSAGE.headline,
          body: ERROR_GENERIC_MESSAGE.headline,
        });
        return undefined;
      } else {
        return response;
      }
    },
    [nextHandler],
  );

  const resetPersonalForm = useCallback(() => {
    resetForm({
      values: {
        ...perosonalDetails,
        userName: '',
      },
    });
  }, [resetForm]);

  const docIdVerifiyHandler = useCallback(
    async (payload: DynamicObjectInterface) => {
      const response = await nextHandler(payload, undefined, true, false, APICallType.PATCH);
      return response;
    },
    [nextHandler],
  );

  const nextButtonHandler = useCallback(() => {
    logger.log(formik.status, formik.isValid, formik.errors, values, 'formik');
    if (values.termsAndConditionCheckbox) {
      const payload: RegistrationPatchRequest = {
        ...(values.termsAndConditionCheckbox && {
          consentAccepted: [RegistrationPatchRequestConsentAcceptedEnum.Terms],
          phone: isPhonePrimaryContact ? values.userName : values.phone,
          email: values.email,
        }),
        version: userData?.patchVersion,
      };
      logger.log('api called', payload);
      nextHandlerCallback(payload, false);
    }
  }, [nextHandler, values.termsAndConditionCheckbox]);

  return (
    <DesktopLayout desktopImage={image} imageClassName={styles['desktop-image']}>
      <div className={styles['coappIdStep-section']} data-testid="id-step">
        <div className={styles['coappIdStep-section-title']}>{t('profile.CoApplicantStepLatam.heading')}</div>
        <div className={'img-form-container'}>
          {[Breakpoints.XS, Breakpoints.SM, Breakpoints.MD].includes(viewport as Breakpoints) && (
            <>
              {image?.url ? (
                <div className={styles['mobile-image']}>
                  <NextgenImage src={image?.url} loaderType={LoaderType.WEBFONTS} alt={'altimage'} fill={true} />
                </div>
              ) : (
                <SkeletonLoader />
              )}
            </>
          )}

          <FormikProvider value={formik}>
            <Form className={styles['coapp-form']}>
              <div className={styles['id-doctype']}>
                <PersonalIdAndTypeInput
                  nextHandlerCallback={docIdVerifiyHandler}
                  resetPersonalForm={resetPersonalForm}
                  isRefreshRestoredData={isRefreshRestoredData}
                  wrapperClass={styles['id-input-fields']}
                />
              </div>

              <RegistrationUsername
                isPhonePrimaryContact={isPhonePrimaryContact}
                setIsPhonePrimaryContact={setIsPhonePrimaryContact}
                showPasswordField={false}
                nextHandler={nextHandler}
                wrappperClass={styles['username-input']}
                showOpsSecondaryField={true}
                showSecondaryContactField={false}
                userNameLabel={t('profile.CoApplicantStepLatam.userNameLabel')}
              />

              <div className={styles['tnc']}>
                <Field type="text" name="termsAndConditionCheckbox">
                  {({ field }: FieldProps) => (
                    <Checkbox
                      {...field}
                      className={styles['tnc-checkbox']}
                      checked={values.termsAndConditionCheckbox}
                      data-testid="tnc-checkbox"
                      text={t('profile.CoApplicantStepLatam.checkboxText')}
                      disabled={!formik?.status?.enableField}
                    />
                  )}
                </Field>
              </div>

              <div className={`${styles['coappIdStep-section-next-button']} ${styles['sticky-bottom-bar']} `}>
                <Button
                  data-testId="next-button-id"
                  title={t('profile.common.nextButton')}
                  buttonStyle={ButtonStyles.SOLID}
                  size={ButtonSize.MEDIUM}
                  buttonWidth={ButtonWidth.FULL_WIDTH}
                  disabled={!formik.isValid || !formik.dirty}
                  onClick={nextButtonHandler}
                />
              </div>
            </Form>
          </FormikProvider>

          {(modalErrorData?.headline || modalErrorData?.body) && <ApiErrorModal {...modalErrorData} />}
        </div>
      </div>
    </DesktopLayout>
  );
};

export default CoApplicantIdStepLatam;
